import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";
import type {
  BriefingDetail,
  BriefingListResponse,
  CalendarResponse,
  CoverageResponse,
  SourceListResponse,
} from "../types/api";

/**
 * Each screen owns its own query, independently loading/erroring — this
 * is what makes a failed Source Health or Coverage call unable to blank
 * out Today (Step 13's explicit requirement). No query here ever throws
 * outside React Query's own error state.
 */

export function useLatestBriefing() {
  return useQuery({
    queryKey: ["briefing", "latest"],
    queryFn: () => apiGet<BriefingDetail>("/briefings/latest"),
    retry: 1,
  });
}

export function useBriefings(limit: number, offset: number) {
  return useQuery({
    queryKey: ["briefings", limit, offset],
    queryFn: () => apiGet<BriefingListResponse>("/briefings", { limit, offset }),
    retry: 1,
  });
}

export function useBriefingDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["briefing", id],
    queryFn: () => apiGet<BriefingDetail>(`/briefings/${id}`),
    enabled: Boolean(id),
    retry: 1,
  });
}

export function useCalendarRange(start: string, end: string) {
  return useQuery({
    queryKey: ["calendar", start, end],
    queryFn: () => apiGet<CalendarResponse>("/calendar", { start, end }),
    retry: 1,
  });
}

export function useSources() {
  return useQuery({
    queryKey: ["sources"],
    queryFn: () => apiGet<SourceListResponse>("/sources"),
    retry: 1,
  });
}

export function useCoverage() {
  return useQuery({
    queryKey: ["system", "coverage"],
    queryFn: () => apiGet<CoverageResponse>("/system/coverage"),
    retry: 1,
  });
}
